<template>
  <div>
    <h3 class="mb-8">
      {{ $t('appointment.confirmAppointment.options') }}
    </h3>

    <v-form @submit.prevent="validate" ref="form">
      <FormYesOrNo
        @changeDecision="setConfirmStatus"
        :initial-value="confirmed"
        :question="$t('appointment.confirmAppointment.form.confirmAppointment')"
      />

      <v-spacer class="mb-7" />

      <div v-if="confirmed">
        <FormYesOrNo
          v-if="realEstateAllowsKeyPickup || isManual"
          @changeDecision="setKeysNeedsAuthorization"
          :initial-value="keysNeedsAuthorization"
          :question="$t('appointment.confirmAppointment.form.keysNeedsAuthorization')"
        />

        <v-spacer v-if="realEstateAllowsKeyPickup || isManual" class="mb-7" />

        <FormYesOrNo
          @changeDecision="setAttendingToAppointment"
          :initial-value="attendingToAppointment"
          :question="$t('appointment.confirmAppointment.form.attendingToAppointment')"
        />

        <v-spacer class="mb-7" />

        <!-- terms -->
        <div v-if="keysNeedsAuthorization && !isManual">
          <p class="mt-6 mb-4">
            {{ $t('appointment.confirmAppointment.form.term.title') }}
          </p>
          <v-textarea
            v-model="terms"
            hide-details
            outlined
            readonly
          />

          <HCheckbox class="mt-6 mb-4" v-model="agreeWithTerms" :label="$t('appointment.confirmAppointment.form.term.agreeWithTerms')" />
        </div>

        <!-- signature -->
        <!-- at manual confirmation, the signature is not available -->
        <div v-if="keysNeedsAuthorization && !isManual" class="signature-wrapper mb-8">
          <p class="mt-6 mb-0">
            {{ $t('appointment.confirmAppointment.form.signature') }}
          </p>
          <small class="d-block mb-3">
            {{ $t('appointment.confirmAppointment.form.signatureExplanation') }}
          </small>

          <v-progress-circular
            v-if="signatureUploading"
            class="signature-loading"
            color="grey"
            width="2"
            size="18"
            indeterminate
          />
          <VueSignaturePad
            :options="{ onBegin, onEnd }"
            class="signature"
            ref="signature"
            height="300px"
          />
          <div v-if="showClearSignature" class="text-right">
            <v-btn @click="clearSignature" color="grey" text>
              <h5>
                {{ $t('appointment.confirmAppointment.form.clearSignature') }}
              </h5>
            </v-btn>
          </div>
          <small v-if="signatureError" class="error--text">
            {{ $t('appointment.confirmAppointment.messages.signatureError') }}
          </small>
        </div>
      </div>

      <!-- comment -->
      <div>
        <p class="mt-6 mb-0">
          {{ $t('appointment.confirmAppointment.form.observation') }}
        </p>
        <small class="d-block grey--text mb-2">
          {{ $t('appointment.confirmAppointment.form.keyAccessNotice') }}
        </small>
        <v-textarea
          v-model="comment"
          class="comment"
          maxlength="1024"
          counter="1024"
          outlined
        />
      </div>

      <v-card v-if="warningMessage" class="confirm-appointment-message pa-5 mb-4" color="orange lighten-5" elevation="0">
        <span class="d-block text-center orange--text">
          {{ warningMessage }}
        </span>
      </v-card>

      <v-btn
        @click="validate"
        :disabled="shouldDisableForm"
        :loading="loading"
        color="primary"
        elevation="0"
        large
        block
      >
        {{ $t('appointment.confirmAppointment.form.submit') }}
      </v-btn>
    </v-form>
  </div>
</template>

<script>
import { uploadToS3 } from '@/services/upload/uploadService'
import { catalogDate, catalogDateWithoutTime, verifyTimePeriod, timeSlot } from '@/util/catalog'
import { required } from '@/util/errorHandling'
import { mapActions, mapGetters, mapMutations } from 'vuex'

import HCheckbox from '@/components/app/HCheckbox'
import HDocumentID from '@/components/app/HDocumentID'
import HPhone from '@/components/app/HPhone'
import FormYesOrNo from './fragments/FormYesOrNo.vue'

import { buildConfirmationFormState } from './confirmAppointmentForm.helpers'

export default {
  name: 'ConfirmAppointmentForm',

  components: {
    FormYesOrNo,
    HDocumentID,
    HPhone,
    HCheckbox,
  },

  props: {
    isManual: {
      type: Boolean,
      required: false,
      default: false,
    },

    appointment: {
      type: Object,
      required: false,
    },

    newAppointmentDateInfo: {
      type: Object | null,
      required: false,
    }
  },

  computed: {
    ...mapGetters(['confirmAppointmentData', 'isLoading']),

    terms() {
      if (this.confirmAppointmentData) {
        const formattedDate = timeSlot(this.confirmAppointmentData?.date, this.confirmAppointmentData?.slot_size, this.confirmAppointmentData?.timezone)

        return this.$t(
          'appointment.confirmAppointment.form.term.text',
          {
            branchName: this.confirmAppointmentData.branch.name,
            scheduledBy: this.confirmAppointmentData.scheduled_by,
            inspector: this.hostName,
            date: formattedDate,
          })
      }

      return ''
    },

    hostName() {
      return this.confirmAppointmentData.host != null ? this.confirmAppointmentData.host.name : '---'
    },

    shouldDisableForm() {
      this.warningMessage = null

      if (this.signatureUploading) {
        return true
      }

      if (this.confirmed == null) {
        return true
      }

      if (this.confirmed == true) {
        if ((this.realEstateAllowsKeyPickup == true || this.isManual) && this.keysNeedsAuthorization == null) {
          return true
        }

        if (this.attendingToAppointment == null) {
          return true
        }

        if (!this.agreeWithTerms && this.keysNeedsAuthorization == true  && !this.isManual) {
          return true
        }

        if (this.keysNeedsAuthorization == true && !this.signatureRequest && !this.isManual) {
          return true
        }

        if (!this.attendingToAppointment && !this.keysNeedsAuthorization) {
          this.warningMessage = this.$t('appointment.confirmAppointment.messages.notAttendingOrKeysWarning')
          return true
        }
      }

      if (this.confirmed == false && !this.comment) {
        this.warningMessage = this.$t('appointment.confirmAppointment.messages.noCommentWarning')
        return true
      }

      return false
    },

    realEstateAllowsKeyPickup() {
      return this.confirmAppointmentData?.real_estate?.allows_key_pickup
    },

    date() {
      return catalogDateWithoutTime(this.confirmAppointmentData?.date)
    },

    period() {
      return verifyTimePeriod(this.confirmAppointmentData?.date)
    },
  },

  methods: {
    ...mapMutations(['closeAllModals']),
    ...mapActions(['confirmationSignatureUploadUrl', 'addAppointmentConfirmation']),

    base64ToBlob(base64String) {
      const appointmentId = this.$route.params.id ?? this.appointment.id
      const base64Content = base64String.split(',')[1]

      const byteCharacters = atob(base64Content)
      const byteNumbers = new Array(byteCharacters.length)

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }

      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray], { type: 'image/png' })
      const file = new File([blob], `signature-${appointmentId}.png`, { type: 'image/png' })

      return file
    },

    async handleBase64ToImageSignature(data) {
      const blob = this.base64ToBlob(data)
      const appointmentId = this.$route.params.id ?? this.appointment.id

      try {
        const { external_filename, url } = await this.confirmationSignatureUploadUrl({
          appointment_id: appointmentId,
          filename: `signature-${appointmentId}.png`,
          filetype: 'image/png',
        })

        await uploadToS3(url, blob)

        this.signatureRequest = {
          external_filename,
          external_url: url,
          filename: `signature-${appointmentId}.png`
        }
      } catch (error) {
        this.signatureError = true
        this.$refs.signature.clearSignature()
      } finally {
        this.signatureUploading = false
        this.showClearSignature = true
      }
    },

    onBegin() {
      clearTimeout(this.timeout)
      this.signatureError = false
      this.signatureUploading = true
      this.showClearSignature = false
    },

    onEnd() {
      this.timeout = setTimeout(() => {
        const { data } = this.$refs.signature.saveSignature()
        if (data) this.handleBase64ToImageSignature(data)
      }, 2500)
    },

    generatePayload() {
      const { id } = this.$route.params

      return {
        appointment_id: id ?? this.appointment.id,
        comment: this.comment,
        confirmed: this.confirmed,
        tenant_name: this.tenantName,
        tenant_email: this.tenantEmail,
        tenant_phone: this.tenantPhone,
        tenant_document: this.tenantDocument,
        keys_needs_authorization: this.keysNeedsAuthorization,
        attending_to_appointment: this.attendingToAppointment,
        signature_request: this.signatureRequest,

        // new appointment date info
        new_date: this.newAppointmentDateInfo?.date,
        new_slot_size: this.newAppointmentDateInfo?.slot_size ?? 0,
        new_group_id: this.newAppointmentDateInfo?.group_id,
      }
    },

    checkParentMethodsAndExecute() {
      const modalRoot = this.$parent?.$parent?.$parent?.$parent?.$parent

      if (modalRoot && modalRoot?.refreshMap) {
        modalRoot.refreshMap()
      }
    },

    validate() {
      if (this.$refs.form.validate()) {
        this.loading = true

        this.addAppointmentConfirmation({ body: this.generatePayload(), isManual: this.isManual })
          .then((success) => {
            if (success) this.$emit('confirmationSubmitted')
            this.closeAllModals()
          })
          .then(() => this.checkParentMethodsAndExecute())
          .finally(() => this.loading = false)
      }
    },

    setKeysNeedsAuthorization(value) {
      if (value === undefined) {
        this.keysNeedsAuthorization = null;
      } else {
        this.keysNeedsAuthorization = !value;
      }
    },

    setAttendingToAppointment(value) {
      if (value === undefined) {
        this.attendingToAppointment = null;
      } else {
        this.attendingToAppointment = !value;
      }
    },

    setConfirmStatus(value) {
      if (value === undefined) {
        this.confirmed = null;
      } else {
        this.confirmed = !value;
      }
    },

    clearSignature() {
      this.$refs.signature.clearSignature()
      this.signatureRequest = null
    },
  },

  data() {
    const confirmation = this.$store.getters.confirmAppointmentData?.confirmation
    const prefill = buildConfirmationFormState(confirmation)

    return {
      timeout: null,
      formatDate: catalogDate,

      loading: false,
      signatureUploading: false,
      signatureError: false,

      file: '',
      comment: prefill.comment,
      tenantName: null,
      tenantEmail: null,
      tenantPhone: null,
      tenantDocument: null,
      signatureRequest: null,

      confirmed: prefill.confirmed,
      attendingToAppointment: prefill.attendingToAppointment,
      keysNeedsAuthorization: prefill.keysNeedsAuthorization,
      agreeWithTerms: false,
      showClearSignature: false,

      rules: [required],

      warningMessage: null,
    }
  },
}
</script>


<style lang="scss" scoped>
.signature {
  width: 100%;
  border-radius: 4px;
  border: 1px solid #ddd;
}

.signature-wrapper {
  position: relative;
}

.signature-loading {
  position: absolute;
  bottom: 10px;
  right: 10px;
}
</style>
